<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { addSample, maxSamples, neighborSamples, removeSample } from '$lib/stores/neighbors';
  import { debounce } from 'lodash';
  import { minQueryLength, suggestCompanies } from '$lib/api/autocomplete';
  import type { Suggestion } from '$lib/types/api';

  let query = '';
  let suggestions: Suggestion[] = [];
  let loading = false;
  let showSearch = false;

  const fetchSuggestions = debounce(async (text: string) => {
    if (text.length < minQueryLength) {
      suggestions = [];
      return;
    }
    loading = true;
    suggestions = await suggestCompanies(text);
    loading = false;
  }, 200);

  const handleInput = (e: Event) => {
    query = (e.currentTarget as HTMLInputElement).value;
    fetchSuggestions(query);
  };

  const selectCompany = (company: Suggestion) => {
    if (!addSample(company)) return;

    query = '';
    suggestions = [];
    showSearch = false;
  };

  const removeCompany = (id: string) => removeSample(id);

  $: selectedCount = $neighborSamples.length;
</script>

<div>
  <div class="p-3">
    <!-- Header -->
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center">
        <svg
          class="mr-2 h-3.5 w-3.5 text-accent-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
        <h3 class="text-xs font-semibold text-gray-900">{$_('samples.title')}</h3>
      </div>
      <span class="text-xs text-gray-500">{selectedCount}/{maxSamples}</span>
    </div>

    <!-- Selected Companies -->
    {#if selectedCount > 0}
      <div class="mb-3 space-y-1">
        {#each $neighborSamples as company (company.id)}
          <div class="flex items-center justify-between rounded-control bg-accent-50 px-2 py-1">
            <span class="truncate text-xs text-accent-700">
              {company.name}
            </span>
            <div
              on:click={() => removeCompany(company.id)}
              on:keydown={(e) => e.key === 'Enter' && removeCompany(company.id)}
              role="button"
              tabindex="0"
              class="ml-2 cursor-pointer text-accent-500 transition-colors hover:text-red-500"
              style="background: none !important; box-shadow: none !important;"
              aria-label={$_('buttons.remove')}
            >
              <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Add Company Button/Search -->
    {#if selectedCount < maxSamples}
      {#if !showSearch}
        <div
          on:click={() => (showSearch = true)}
          on:keydown={(e) => e.key === 'Enter' && (showSearch = true)}
          role="button"
          tabindex="0"
          class="w-full cursor-pointer rounded-control border border-dashed border-accent-300 bg-accent-50 px-3 py-2 text-xs font-medium text-accent-600 transition-all hover:border-accent-400 hover:bg-accent-100"
          style="background: none !important; background-color: transparent !important; box-shadow: none !important;"
        >
          <span class="flex items-center justify-center">
            <svg class="mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {$_('samples.add')}
          </span>
        </div>
      {:else}
        <div class="relative">
          <input
            class="w-full rounded-control border border-gray-300 bg-white px-3 py-1.5 text-xs placeholder-gray-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            type="text"
            placeholder={$_('form.placeholders.searchCompany')}
            bind:value={query}
            on:input={handleInput}
            on:blur={() =>
              setTimeout(() => {
                if (!query) showSearch = false;
              }, 200)}
            autofocus
          />

          {#if loading}
            <div class="absolute right-2 top-1/2 -translate-y-1/2">
              <svg class="h-3 w-3 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          {/if}
        </div>

        {#if suggestions.length > 0}
          <div
            class="mt-1 max-h-36 overflow-y-auto rounded-control border border-hairline bg-surface-panel shadow-overlay shadow-lg"
          >
            {#each suggestions as suggestion}
              <div
                class={`flex w-full items-center justify-between px-3 py-2 text-left transition-colors ${
                  suggestion.available
                    ? 'cursor-pointer hover:bg-accent-50'
                    : 'cursor-not-allowed opacity-50'
                }`}
                on:click={() => suggestion.available && selectCompany(suggestion)}
                on:keydown={(e) =>
                  e.key === 'Enter' && suggestion.available && selectCompany(suggestion)}
                role="button"
                tabindex={suggestion.available ? 0 : -1}
                style="background: none !important; box-shadow: none !important;"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate text-xs font-medium text-gray-900">{suggestion.name}</div>
                </div>
                {#if !suggestion.available}
                  <span class="ml-2 text-xs text-gray-400">{$_('samples.unavailable')}</span>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {/if}
    {/if}

    <!-- Info text -->
    {#if selectedCount === 0}
      <p class="mt-2 text-xs text-gray-500">{$_('samples.hint')}</p>
    {:else if selectedCount === 1}
      <p class="mt-2 text-xs text-gray-500">
        {$_('samples.ready')}
      </p>
    {/if}
  </div>
</div>
